import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../../components/Header/Header";
import images from "../../../images";
import { StatsWidget } from "./StatsWidget";
import { QuestionCard } from "./QuestionCard";
import {
  useGetQuestDetailsQuery,
  useGetQuestQuestionsDetailsQuery,
  usePostActionEditQuestDetailsMutation,
} from "../../../services/apiSlice";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import { useNavigate } from "react-router-dom";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatQuestionType } from "../../../Utils/helper";
import Button from "../../../components/common/Button/Button";

const QuestDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const optionQuestDetailsModalRef = useRef(null);
  const confirmModalRef = useRef(null);
  const [selectedAction, setSelectedAction] = useState(null);

  const [postActionEditQuest] = usePostActionEditQuestDetailsMutation();
  const { data: questDetailsData, isLoading: isLoadingDetails } =
    useGetQuestDetailsQuery({
      quest_id: id,
    });

  const { data: questQuestionsData, isLoading: isLoadingQuestions } =
    useGetQuestQuestionsDetailsQuery(
      { quest_id: questDetailsData?.data?.id },
      { skip: !questDetailsData?.data?.id }
    );

  const questQuestionDataList = questQuestionsData?.data || [];

  const handleOptionQuestDetails = () => {
    optionQuestDetailsModalRef.current?.openModal();
  };

  const handleOpenConfirmModal = (action) => {
    setSelectedAction(action);
    optionQuestDetailsModalRef.current?.closeModal();
    confirmModalRef.current?.openModal();
  };

  const handleQuestAction = async (action) => {
    try {
      const payload = {
        quest_id: id,
        action: action,
      };
      const response = await postActionEditQuest(payload).unwrap();

      if (response?.status === 200) {
        toast.success(
          response?.data?.message ||
            `${action.replace("_", " ")} ${t(
              "quest.questDetails.actionCompletedSuccessfully"
            )}`
        );

        confirmModalRef.current?.closeModal();
        navigate("/");
      } else {
        throw new Error(response?.data.message || "Action failed");
      }
    } catch (error) {
      console.error(`Failed to ${action} quest:`, error);
      toast.error(
        error?.message ||
          `${t("quest.questDetails.failedTo")} ${action.replace("_", " ")} ${t(
            "quest.questDetails.quest"
          )}`
      );
      confirmModalRef.current?.closeModal();
    }
  };

  // Define action availability based on status_biz
  const statusBiz = questDetailsData?.data?.status_biz;
  const isActionDisabled = (action) => {
    switch (action) {
      case "delete":
        return !["DRAFT", "PUBLISHED"].includes(statusBiz);
      case "end_quest":
        return ["COMPLETED"].includes(statusBiz);
      case "archive":
        return ["ARCHIVED"].includes(statusBiz);
      default:
        return true;
    }
  };

  return (
    <main className="flex flex-col mx-auto w-full max-w-[480px] min-h-screen">
      <LoadingCommon statusLoading={isLoadingDetails || isLoadingQuestions} />
      <div className="sticky top-0 z-10 bg-white w-full max-w-[480px] mx-auto">
        <Header
          subTitle={t("quest.questDetails.title")}
          handleBackIconSubtitle={() => navigate("/")}
          backIconSubtitle={images.ArrowLeftIcon}
          subTitleIcon={images.ThreeDotIcon}
          onSubTitleClick={handleOptionQuestDetails}
          className={"justify-end max-w-[480px]"}
        />
      </div>

      <div className="flex flex-col justify-between p-3.5 w-full">
        <section className="self-center max-w-full w-[396px]">
          {(questQuestionDataList || []).map((question, index) => (
            <div key={index} className={index > 0 ? "mt-4" : ""}>
              <QuestionCard
                {...question}
                name={"Question " + (index + 1)}
                type={formatQuestionType(question?.type)}
                options={question.quest_question_option}
              />
            </div>
          ))}
        </section>
        <StatsWidget
          maxSlot={questDetailsData?.data?.max_slot}
          token={questDetailsData?.data?.token}
          expiryTime={questDetailsData?.data?.expire_time}
          location={questDetailsData?.data?.quest_locations?.length}
          incentives={questDetailsData?.data?.incentives}
        />
      </div>

      {/* Options Modal */}
      <CommonModal ref={optionQuestDetailsModalRef} className="w-[195px]">
        <div className="flex flex-col justify-between gap-2">
          {[
            { action: "delete", label: t("base.delete") },
            { action: "end_quest", label: t("quest.questDetails.endQuest") },
            { action: "archive", label: t("quest.questDetails.archive") },
          ].map(({ action, label }) => (
            <h2
              key={action}
              className={`cursor-pointer text-sm ${
                isActionDisabled(action)
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-black hover:text-indigo-600"
              }`}
              onClick={() =>
                !isActionDisabled(action) && handleOpenConfirmModal(action)
              }
            >
              {label}
            </h2>
          ))}
        </div>
      </CommonModal>

      {/* Confirmation Modal */}
      <CommonModal
        ref={confirmModalRef}
        // title={t("quest.questDetails.confirmAction", {
        //   action: selectedAction?.replace("_", " "),
        // })}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] bg-white border border-solid border-stone-200 text-neutral-500"
              onClick={() => {
                setSelectedAction(null);
                closeModal();
              }}
            />
            <Button
              text={t("base.yes")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-indigo-600"
              variant="primary"
              onClick={() => {
                if (selectedAction) {
                  handleQuestAction(selectedAction);
                }
              }}
            />
          </>
        )}
        className="w-[300px]"
      >
        <p className="text-sm text-gray-600">
          {t("quest.questDetails.confirmMessage", {
            action: selectedAction?.replace("_", " "),
          })}
        </p>
      </CommonModal>
    </main>
  );
};

export default QuestDetails;
